import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CalendarCheck, Check, Copy, Mail, PartyPopper } from 'lucide-react'
import { Alert, Button, Card, CardBody, CardHeader, Stepper } from '@/components/ui'
import { PageHeader } from '@/components/domain'
import { useT } from '@/internationalization/i18n'
import { toast } from '@/stores'

const STEP_IDS = ['connect', 'invite', 'rules', 'done'] as const

const BOT_ADDRESS = 'sabha.ai@pssgway.com'

/**
 * First-run setup.
 *
 * Step 2 is the one that matters and the one users skip: the recorder joins
 * nothing it was not invited to, so a workspace that connects a calendar and
 * stops sees no recordings and concludes the product is broken. The invite
 * address is therefore the largest, most copyable thing on the page.
 */
export function OnboardingPage() {
  const t = useT()
  const navigate = useNavigate()
  const [step, setStep] = useState(0)

  const steps = STEP_IDS.map((id) => ({
    id,
    label: t(`onboarding.step.${id}`),
    description: t(`onboarding.step.${id}Desc`),
  }))

  const copyAddress = async () => {
    await navigator.clipboard.writeText(BOT_ADDRESS)
    toast.success(t('onboarding.toast.addressCopied'))
  }

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        title={t('onboarding.title')}
        description={t('onboarding.subtitle')}
      />

      <Stepper steps={steps} activeIndex={step} className="mb-6" />

      {step === 0 && (
        <Card>
          <CardHeader
            title={t('onboarding.connectTitle')}
            description={t('onboarding.connectDesc')}
          />
          <CardBody className="space-y-3">
            <Button fullWidth size="lg" leftIcon={<CalendarCheck className="h-4 w-4" />} onClick={() => setStep(1)}>
              {t('onboarding.connectM365')}
            </Button>
            <Button fullWidth size="lg" leftIcon={<CalendarCheck className="h-4 w-4" />} onClick={() => setStep(1)}>
              {t('onboarding.connectGoogle')}
            </Button>
            <p className="text-sm text-fg-subtle">
              {t('onboarding.connectNote')}
            </p>
          </CardBody>
        </Card>
      )}

      {step === 1 && (
        <Card>
          <CardHeader title={t('onboarding.inviteTitle')} description={t('onboarding.inviteDesc')} />
          <CardBody className="space-y-4">
            <div className="flex flex-col gap-2 rounded-lg border border-border bg-surface-muted p-4 sm:flex-row sm:items-center">
              <Mail aria-hidden className="h-5 w-5 shrink-0 text-fg-muted" />
              <code className="min-w-0 flex-1 truncate font-mono text-md text-fg">{BOT_ADDRESS}</code>
              <Button leftIcon={<Copy className="h-4 w-4" />} onClick={copyAddress}>
                {t('onboarding.copy')}
              </Button>
            </div>
            <Alert tone="info" title={t('onboarding.addAsAttendeeTitle')}>
              {t('onboarding.addAsAttendeeBody')}
            </Alert>
            <Button variant="primary" fullWidth onClick={() => setStep(2)}>
              {t('onboarding.iHaveInvited')}
            </Button>
          </CardBody>
        </Card>
      )}

      {step === 2 && (
        <Card>
          <CardHeader title={t('onboarding.chooseTitle')} description={t('onboarding.chooseDesc')} />
          <CardBody className="space-y-3">
            {[
              [t('onboarding.choice.explicit'), t('onboarding.choice.explicitDesc')],
              [t('onboarding.choice.external'), t('onboarding.choice.externalDesc')],
              [t('onboarding.choice.everything'), t('onboarding.choice.everythingDesc')],
            ].map(([title, description], index) => (
              <button
                key={title}
                type="button"
                onClick={() => setStep(3)}
                className="flex w-full items-start gap-3 rounded-md border border-border p-3 text-left
                  transition-colors hover:border-brand hover:bg-brand-subtle/40"
              >
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-border-strong text-2xs">
                  {index + 1}
                </span>
                <span>
                  <span className="block text-base font-medium text-fg">{title}</span>
                  <span className="block text-sm text-fg-muted">{description}</span>
                </span>
              </button>
            ))}
          </CardBody>
        </Card>
      )}

      {step === 3 && (
        <Card>
          <CardBody className="flex flex-col items-center py-10 text-center">
            <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-success-subtle text-success">
              <PartyPopper aria-hidden className="h-6 w-6" />
            </span>
            <h2 className="text-lg font-semibold text-fg">{t('onboarding.doneTitle')}</h2>
            <p className="mt-1 max-w-md text-base text-fg-muted">
              {t('onboarding.doneBody')}
            </p>
            <Button variant="primary" className="mt-5" leftIcon={<Check className="h-4 w-4" />} onClick={() => navigate('/')}>
              {t('onboarding.goToWorkspace')}
            </Button>
          </CardBody>
        </Card>
      )}
    </div>
  )
}
